import { Button } from '@ads/components-react';
import { ChevronDownIcon, ChevronUpDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  type PaginationState,
  type Row,
  type SortingState,
  useReactTable,
} from '@tanstack/react-table';
import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Skeleton } from '@/shared/ui/atoms';
import { EmptyState } from '@/shared/ui/molecules/EmptyState';

// ─── Tipos ───────────────────────────────────────────────────────────────────

/** Props genéricas do DataTable */
export interface DataTableProps<TData> {
  /** Dados a exibir na tabela */
  readonly data: TData[];
  /** Definição das colunas (TanStack Table ColumnDef) */
  readonly columns: ColumnDef<TData, unknown>[];
  /** Estado de carregamento — exibe skeleton quando true */
  readonly isLoading?: boolean;
  /** Total de registros para paginação server-side */
  readonly totalCount?: number;
  /** Página atual (1-indexed) */
  readonly currentPage?: number;
  /** Total de páginas */
  readonly totalPages?: number;
  /** Mensagem exibida quando não há dados */
  readonly emptyStateMessage?: string;
  /** Descrição opcional do estado vazio */
  readonly emptyStateDescription?: string;
  /** Ação opcional do estado vazio (ex: botão de criar) */
  readonly emptyStateAction?: ReactNode;
  /** Ilustração customizada para o estado vazio */
  readonly emptyStateIllustration?: string;
  /**
   * Conteúdo opcional renderizado **dentro** do card, logo abaixo da tabela —
   * totais, contagens ou uma ação que age sobre as linhas.
   *
   * Ocupa o lugar que a paginação ocuparia, e por isso reaproveita a borda
   * inferior do `<tbody>` como separador. Sem ele, uma tabela sem paginação
   * termina com essa borda pendurada sobre o padding do card.
   */
  readonly footer?: ReactNode;
  /** Callback ao clicar em uma linha */
  readonly onRowClick?: (row: Row<TData>) => void;
  /** Callback de mudança de página */
  readonly onPageChange?: (page: number) => void;
  /** Número de linhas do skeleton de loading */
  readonly skeletonRowCount?: number;
  /** Classe CSS adicional aplicada ao container externo da tabela */
  readonly className?: string | undefined;
  /**
   * Ativa table-layout: fixed e aplica larguras definidas via `size` nas ColumnDefs.
   * Colunas sem `size` explícito ocupam o espaço restante.
   */
  readonly tableFixed?: boolean;
  /**
   * Itens por página usados no texto "Exibindo X–Y de Z registros".
   * Deve refletir o `PageSize` real enviado à API. Default: 20.
   */
  readonly pageSize?: number;
  /**
   * Ativa ordenação client-side (na página atual) com indicadores de seta nos
   * cabeçalhos. As colunas ordenáveis são controladas por `enableSorting` na
   * própria `ColumnDef` (default do TanStack: ordenável). Default: false.
   */
  readonly enableSorting?: boolean;
}

// ─── Componente ───────────────────────────────────────────────────────────────

/**
 * Organism DataTable — tabela genérica com TanStack Table.
 *
 * Encapsula a configuração do TanStack Table, renderização de linhas/colunas,
 * estados de loading (skeleton), empty state e paginação server-side.
 *
 * Sem lógica de domínio — para tabelas específicas, crie um organismo de feature
 * que usa este DataTable como base.
 *
 * @example
 * ```tsx
 * <DataTable
 *   data={users}
 *   columns={userColumns}
 *   isLoading={isLoading}
 *   totalCount={total}
 *   currentPage={page}
 *   totalPages={pages}
 *   onPageChange={setPage}
 *   onRowClick={(row) => navigate({ to: '/users/$id', params: { id: row.original.id } })}
 * />
 * ```
 */
export function DataTable<TData>({
  data,
  columns,
  isLoading = false,
  totalCount = 0,
  currentPage = 1,
  totalPages = 1,
  emptyStateMessage,
  emptyStateDescription,
  emptyStateAction,
  emptyStateIllustration,
  footer,
  onRowClick,
  onPageChange,
  skeletonRowCount = 6,
  className,
  tableFixed = false,
  pageSize = 20,
  enableSorting = false,
}: DataTableProps<TData>) {
  const { t } = useTranslation('common');
  const [sorting, setSorting] = useState<SortingState>([]);

  const table = useReactTable({
    data,
    columns,
    manualPagination: true,
    pageCount: totalPages,
    state: {
      pagination: {
        pageIndex: currentPage - 1, // TanStack usa 0-indexed
        pageSize,
      },
      ...(enableSorting ? { sorting } : {}),
    },
    onPaginationChange: updater => {
      if (typeof updater === 'function') {
        const next = updater({
          pageIndex: currentPage - 1,
          pageSize,
        } as PaginationState);
        onPageChange?.(next.pageIndex + 1);
      }
    },
    ...(enableSorting
      ? { onSortingChange: setSorting, getSortedRowModel: getSortedRowModel() }
      : {}),
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div
      className={`w-full overflow-hidden rounded-sm border-hairline border-ctx-layout-border bg-ctx-layout-surface p-xxxs${className ? ` ${className}` : ''}`}
    >
      {/* Tabela */}
      <div className="overflow-x-auto">
        <table className={`w-full text-xxs${tableFixed ? ' table-fixed' : ''}`}>
          {/* Header */}
          <thead>
            {table.getHeaderGroups().map(headerGroup => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map(header => {
                  const canSort = enableSorting && header.column.getCanSort();
                  const sorted = header.column.getIsSorted();
                  const SortIcon =
                    sorted === 'asc'
                      ? ChevronUpIcon
                      : sorted === 'desc'
                        ? ChevronDownIcon
                        : ChevronUpDownIcon;
                  const content = header.isPlaceholder
                    ? null
                    : flexRender(header.column.columnDef.header, header.getContext());

                  return (
                    <th
                      key={header.id}
                      className="px-xxs py-micro text-left align-middle text-ctx-content-title ts-subtitle-xxs"
                      scope="col"
                      style={
                        tableFixed && header.column.columnDef.size !== undefined
                          ? { width: `${header.column.columnDef.size}px` }
                          : undefined
                      }
                    >
                      {canSort ? (
                        <button
                          type="button"
                          onClick={header.column.getToggleSortingHandler()}
                          className="flex items-center gap-quark"
                        >
                          {content}
                          <SortIcon
                            className={`h-micro w-micro shrink-0 ${sorted ? 'text-ctx-content-base' : 'text-ctx-content-placeholder'}`}
                          />
                        </button>
                      ) : (
                        content
                      )}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>

          {/* Body */}
          <tbody className="border-y-hairline border-ctx-layout-border bg-ctx-layout-surface">
            {isLoading ? (
              // Skeleton rows
              Array.from({ length: skeletonRowCount }).map((_, i) => (
                <tr key={i}>
                  {columns.map((_, j) => (
                    <td key={j} className="h-[4.5rem] px-xxs py-mini align-middle">
                      <Skeleton className="h-mini w-full" />
                    </td>
                  ))}
                </tr>
              ))
            ) : table.getRowModel().rows.length === 0 ? (
              // Empty state
              <tr>
                <td colSpan={columns.length}>
                  <EmptyState
                    title={emptyStateMessage ?? t('table.empty')}
                    description={emptyStateDescription}
                    action={emptyStateAction}
                    illustration={emptyStateIllustration}
                  />
                </td>
              </tr>
            ) : (
              // Data rows
              table.getRowModel().rows.map(row => (
                <tr
                  key={row.id}
                  onClick={() => onRowClick?.(row)}
                  className={`border-b-hairline border-ctx-layout-border last:border-b-none transition-colors ${onRowClick ? 'cursor-pointer hover:bg-ctx-interactive-secondaryHover' : ''}`}
                >
                  {row.getVisibleCells().map(cell => (
                    <td
                      key={cell.id}
                      className={`h-[4.5rem] px-xxs py-mini align-middle text-ctx-content-base${tableFixed ? ' overflow-hidden' : ''}`}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Rodapé da tabela — totais, contagens ou ação sobre as linhas */}
      {footer && <div className="px-mini py-micro">{footer}</div>}

      {/* Paginação */}
      {totalPages > 1 && (
        <div className="flex flex-col gap-xs border-t-hairline border-ctx-layout-border px-mini py-micro sm:flex-row sm:items-center sm:justify-between">
          <p className="text-xxs text-ctx-content-placeholder">
            {t('table.pagination.showing', {
              from: totalCount === 0 ? 0 : (currentPage - 1) * pageSize + 1,
              to: (currentPage - 1) * pageSize + data.length,
              total: totalCount,
            })}
          </p>
          <div className="flex items-center gap-nano">
            <Button
              hierarchy="secondary"
              size="sm"
              onClick={() => onPageChange?.(currentPage - 1)}
              disabled={currentPage <= 1}
            >
              ← {t('table.pagination.previous')}
            </Button>
            <span className="text-ctx-content-base ts-caption-xxs">
              {currentPage} / {totalPages}
            </span>
            <Button
              hierarchy="secondary"
              size="sm"
              onClick={() => onPageChange?.(currentPage + 1)}
              disabled={currentPage >= totalPages}
            >
              {t('table.pagination.next')} →
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
